// Enemy manager + the four standard enemy types (trooper, hornet, lancer, bulwark)
// with elite affixes. Every attack is telegraphed with shape + animation + colour
// through ctx.fx before it lands.

import Phaser from 'phaser';
import { TEX } from '../../art/keys';
import { DEPTH, FONT } from '../../config/constants';
import { COLORS, css } from '../../config/palette';
import type { EnemyApi, EnemyManagerApi, GameContext, HitArea, TelegraphHandle } from '../../core/contracts';
import type { DamageInfo, DamageResult, EliteAffix, EnemyId, StatusId, StatusState } from '../../core/types';
import {
  AFFIX,
  BULWARK_AI,
  ELITE_AFFIXES_DEF,
  ENEMIES,
  HORNET_AI,
  LANCER_AI,
  TROOPER_AI,
  computeEnemyStats,
  volatileBlastDamage,
  type EnemyDef,
  type EnemyStats,
} from '../../data/enemies';
import { ensureEnemyArt } from './art';
import { ENEMY_ART_SCALE, ENEMY_FX, ENEMY_SIZE, RIGS, enemyAnimKey, frameMeta, frameName, outlineFrame, type EnemyAnim } from './rig';

let nextUid = 1;

type Phase = 'move' | 'windup' | 'attack' | 'recover' | 'stagger' | 'leap';

class Enemy implements EnemyApi {
  readonly uid = nextUid++;
  readonly kind = 'enemy' as const;
  readonly name: string;
  readonly statuses = new Map<StatusId, StatusState>();
  readonly obj: Phaser.GameObjects.Zone;
  readonly body: Phaser.Physics.Arcade.Body;
  readonly flying: boolean;
  readonly knockbackResist: number;
  readonly maxHp: number;
  readonly scrapValue: number;
  hp: number;
  armorHp: number;
  alive = true;
  spawning = false;
  dying = false;

  private sprite: Phaser.GameObjects.Sprite;
  private outline: Phaser.GameObjects.Sprite | null = null;
  private gun: Phaser.GameObjects.Image | null = null;
  private shield: Phaser.GameObjects.Image | null = null;
  private hex: Phaser.GameObjects.Image | null = null;
  private label: Phaser.GameObjects.Text | null = null;
  private barBg: Phaser.GameObjects.Rectangle;
  private bar: Phaser.GameObjects.Rectangle;
  private barTimer = 0;

  private facing: 1 | -1 = 1;
  private anim: EnemyAnim = 'idle';
  private phase: Phase = 'move';
  private phaseT = 0;
  private cooldown: number;
  private knockT = 0;
  private aimAngle = 0;
  private lockAngle = 0;
  private lockX = 0;
  private lockY = 0;
  private shots = 0;
  private shotT = 0;
  private tele: TelegraphHandle | null = null;
  private orbit = Math.random() * Math.PI * 2;
  private t = 0;
  private buzzT = 4;
  private dashDir = 1;
  private dashLeft = 0;
  private turnT = 0;
  private swiftT = 0;
  // Tactical state (see the AI section).
  /** Cached line-of-sight result, refreshed every few frames. */
  private sees = true;
  private seeT = 0;
  private noSightT = 0;
  private strafeDir = 0;
  private strafeT = 0;
  private dodgeCd = 0;
  private jumpCd = 0;
  /** > 0 while dropping through a one-way platform. */
  private dropT = 0;
  /** Per-unit offset of the preferred range (squads spread out). */
  private rangeBias = 0;
  /** Lancer: seconds until it re-evaluates its perch. */
  private perchT = 0;
  /** Hornet: dives chained since the last recover. */
  private chain = 0;
  /** Lead fraction rolled for the current attack. */
  private leadK = 0.8;
  /** Trooper burst: the un-led angle, fired as the bracketing shot. */
  private rawAngle = 0;

  constructor(
    private readonly ctx: GameContext,
    private readonly mgr: EnemyManager,
    readonly defId: EnemyId,
    x: number,
    y: number,
    readonly elite: readonly EliteAffix[],
    private readonly stats: EnemyStats,
  ) {
    const def = ENEMIES[defId];
    const scene = ctx.scene;
    const rig = RIGS[defId];
    this.name = def.name;
    this.flying = def.flying;
    this.knockbackResist = def.knockbackResist;
    this.maxHp = stats.maxHp;
    this.hp = stats.maxHp;
    this.armorHp = stats.maxArmor;
    this.scrapValue = stats.scrap;
    this.cooldown = this.firstDelay();
    this.rangeBias = ctx.rng.float(-TROOPER_AI.rangeJitter, TROOPER_AI.rangeJitter);
    this.perchT = ctx.rng.float(LANCER_AI.perchInterval[0], LANCER_AI.perchInterval[1]);

    const w = def.body.w * stats.scale * ENEMY_SIZE[defId].body;
    const h = def.body.h * stats.scale * ENEMY_SIZE[defId].body;
    // Ground spawns are feet positions; flyers are centres.
    const cy = this.flying ? y : y - h / 2;
    this.obj = scene.add.zone(x, cy, w, h);
    scene.physics.add.existing(this.obj);
    this.body = this.obj.body as Phaser.Physics.Arcade.Body;
    this.body.setAllowGravity(true);
    this.body.setGravityY(this.flying ? 0 : def.gravity);
    this.body.setMaxVelocity(1400, 1600);

    const tex = scene.textures.exists(rig.key) ? rig.key : ENEMY_FX.placeholder;
    if (this.elite.length && scene.textures.exists(rig.key)) {
      this.outline = scene.add.sprite(x, y, rig.key, outlineFrame(frameName('idle', 0))).setDepth(DEPTH.ENEMIES - 0.1);
      this.outline.setOrigin(rig.anchorX / rig.cellW, rig.anchorY / rig.cellH).setTint(ELITE_AFFIXES_DEF[this.elite[0]].color);
      this.outline.setBlendMode(Phaser.BlendModes.ADD);
    }
    this.sprite = scene.add.sprite(x, y, tex, scene.textures.exists(rig.key) ? frameName('idle', 0) : undefined).setDepth(DEPTH.ENEMIES);
    if (tex === rig.key) this.sprite.setOrigin(rig.anchorX / rig.cellW, rig.anchorY / rig.cellH);
    if (rig.gun && scene.textures.get(rig.key).has('gun')) {
      this.gun = scene.add.image(x, y, rig.key, 'gun').setDepth(DEPTH.ENEMIES + 0.1);
      this.gun.setOrigin(rig.gun.pivotX / rig.gun.w, rig.gun.pivotY / rig.gun.h);
    }
    if (rig.shield && scene.textures.exists(rig.shield.key)) {
      this.shield = scene.add.image(x, y, rig.shield.key).setDepth(DEPTH.ENEMIES + 0.2);
      this.shield.setOrigin(rig.shield.gripX / rig.shield.w, rig.shield.gripY / rig.shield.h);
    }
    if (this.armorHp > 0 && scene.textures.exists(ENEMY_FX.hex)) {
      this.hex = scene.add.image(x, cy, ENEMY_FX.hex).setDepth(DEPTH.ENEMIES + 0.3).setTint(COLORS.shield).setBlendMode(Phaser.BlendModes.ADD);
    }
    if (this.elite.length) {
      const names = this.elite.map((a) => ELITE_AFFIXES_DEF[a].name).join(' · ');
      this.label = scene.add
        .text(x, y, `ELITE  ${names}`, { fontFamily: FONT.display, fontSize: '15px', fontStyle: '700', color: css(ELITE_AFFIXES_DEF[this.elite[0]].color), stroke: '#05070b', strokeThickness: 4 })
        .setOrigin(0.5, 1)
        .setDepth(DEPTH.WORLD_UI - 1);
    }
    this.barBg = scene.add.rectangle(x, y, 44, 5, 0x05070b, 0.8).setDepth(DEPTH.WORLD_UI - 2).setVisible(false);
    this.bar = scene.add.rectangle(x, y, 44, 3, COLORS.threat).setOrigin(0, 0.5).setDepth(DEPTH.WORLD_UI - 2).setVisible(false);
    this.play('idle');
  }

  get x(): number {
    return this.body.center.x;
  }
  get y(): number {
    return this.body.center.y;
  }
  get radius(): number {
    return Math.max(this.body.width, this.body.height) * 0.5;
  }
  private get def(): EnemyDef {
    return ENEMIES[this.defId];
  }
  get feetY(): number {
    return this.body.bottom;
  }

  private firstDelay(): number {
    const r = { trooper: TROOPER_AI.firstShotDelay, hornet: HORNET_AI.firstDiveDelay, lancer: LANCER_AI.firstShotDelay, bulwark: BULWARK_AI.firstBashDelay }[this.defId];
    return this.ctx.rng.float(r[0], r[1]);
  }

  setSpawning(on: boolean): void {
    this.spawning = on;
    this.body.enable = !on;
    for (const o of this.visuals()) o.setVisible(!on);
    if (!on) this.barBg.setVisible(false), this.bar.setVisible(false);
  }

  private visuals(): (Phaser.GameObjects.Components.Visible & Phaser.GameObjects.GameObject)[] {
    return [this.sprite, this.outline, this.gun, this.shield, this.hex, this.label].filter(Boolean) as never[];
  }

  hitAreas(): readonly HitArea[] {
    const b = this.body;
    return [{ x: b.x, y: b.y, w: b.width, h: b.height, mult: 1 }];
  }

  /** Bulwark frontal shield: blocks shots from its facing side while raised. */
  blocks(fromX: number, fromY: number): boolean {
    if (this.defId !== 'bulwark' || !this.alive || this.phase === 'recover' || this.phase === 'stagger') return false;
    const front = (fromX - this.x) * this.facing > 0;
    const notAbove = fromY > this.body.top - 20;
    return front && notAbove;
  }

  receiveDamage(amount: number, info: DamageInfo): DamageResult {
    if (!this.alive || this.spawning) return { dealt: 0, killed: false, blocked: true, absorbed: 0 };
    let rest = amount;
    let absorbed = 0;
    if (this.armorHp > 0) {
      absorbed = Math.min(this.armorHp, rest);
      this.armorHp -= absorbed;
      rest -= absorbed;
      if (this.armorHp <= 0 && this.hex) {
        this.ctx.fx.ring(this.x, this.y, 70, { color: COLORS.shield, duration: 260 });
        this.hex.destroy();
        this.hex = null;
      }
    }
    this.hp -= rest;
    this.barTimer = 2.5;
    if (!info.isDot) {
      this.ctx.fx.hitFlash(this.sprite, 60);
      // Heavy hits interrupt a wind-up (poise).
      if (amount >= this.maxHp * this.stats.poise && this.phase !== 'attack' && this.defId !== 'bulwark') this.enter('stagger');
    }
    const killed = this.hp <= 0;
    if (killed) this.die();
    return { dealt: amount, killed, blocked: false, absorbed };
  }

  knockback(vx: number, vy: number): void {
    if (!this.alive) return;
    const k = 1 - this.knockbackResist;
    this.body.velocity.x += vx * k;
    this.body.velocity.y += vy * k * (this.flying ? 1 : 0.6);
    this.knockT = 0.18 * k;
  }

  pull(ax: number, ay: number, dt: number): void {
    if (!this.alive) return;
    const k = 1 - this.knockbackResist * 0.6;
    this.body.velocity.x += ax * dt * k;
    this.body.velocity.y += ay * dt * k;
    this.knockT = Math.max(this.knockT, 0.05);
  }

  private die(): void {
    if (this.dying) return;
    this.alive = false;
    this.dying = true;
    this.tele?.cancel();
    const { ctx } = this;
    const x = this.x;
    const y = this.y;
    ctx.fx.explosion(x, y, 46 * this.stats.scale, COLORS.threatHot);
    ctx.fx.debris(x, y, { count: 6, color: COLORS.steel3 });
    ctx.fx.sparks(x, y, -Math.PI / 2, { count: 10, color: COLORS.threatHot, spread: Math.PI });
    ctx.audio.play('enemy_death', { volume: this.elite.length ? 1 : 0.8 });
    this.body.enable = false;
    if (this.elite.includes('volatile')) {
      const r = AFFIX.volatile.radius;
      ctx.fx.warnCircle(x, y, r, AFFIX.volatile.fuseMs, { color: COLORS.threatHot });
      ctx.scene.time.delayedCall(AFFIX.volatile.fuseMs, () => {
        if (!ctx.scene.sys.isActive()) return;
        ctx.damage.explode(x, y, r, { amount: volatileBlastDamage(this.stats.power), tags: ['explosive'], source: 'enemy', knockback: AFFIX.volatile.knockback, label: 'volatile' }, { hurtsPlayer: true, color: COLORS.threatHot });
      });
    }
    ctx.scene.tweens.add({
      targets: this.visuals(),
      alpha: 0,
      duration: 260,
      onComplete: () => this.destroy(),
    });
    this.barBg.setVisible(false);
    this.bar.setVisible(false);
    this.mgr.unregister(this);
  }

  destroy(): void {
    this.tele?.cancel();
    for (const o of [this.sprite, this.outline, this.gun, this.shield, this.hex, this.label, this.barBg, this.bar, this.obj]) o?.destroy();
  }

  // ───────────── AI ─────────────

  private status(id: StatusId): StatusState | undefined {
    const s = this.statuses.get(id);
    return s && s.remaining > 0 ? s : undefined;
  }

  private speed(): number {
    const slow = this.status('slow');
    return this.stats.speed * (slow ? Math.max(0.15, 1 - slow.potency) : 1);
  }

  private enter(phase: Phase, t = 0): void {
    this.phase = phase;
    this.phaseT = t;
    if (phase !== 'windup') {
      this.tele?.cancel();
      this.tele = null;
    }
    if (phase === 'stagger') this.phaseT = 0.35;
  }

  private play(anim: EnemyAnim): void {
    const key = enemyAnimKey(this.defId, anim);
    if (this.anim === anim && this.sprite.anims.isPlaying) return;
    this.anim = anim;
    if (this.ctx.scene.anims.exists(key)) this.sprite.play(key, true);
  }

  private canSee(): boolean {
    const p = this.ctx.player;
    const hit = this.ctx.world.raycast(this.x, this.y - 10, p.x, p.y);
    return !hit.hit;
  }

  /** Line of sight, refreshed every 0.12 s (raycasts are not free); also tracks how long it has been lost. */
  private sight(dt: number): boolean {
    this.seeT -= dt;
    if (this.seeT <= 0) {
      this.seeT = 0.12;
      this.sees = this.canSee();
    }
    this.noSightT = this.sees ? 0 : this.noSightT + dt;
    return this.sees;
  }

  /** True while it wants to fall through the one-way platform under it (world collider callback). */
  get dropping(): boolean {
    return this.dropT > 0;
  }

  /**
   * Where the player will be when a shot fired now from (fromX, fromY) at `speed` arrives, leading
   * by fraction `k` of their velocity (two intercept iterations). Grounded players are led only
   * horizontally; airborne ones half vertically (gravity bends their arc back).
   */
  private predict(fromX: number, fromY: number, speed: number, k: number, extraT = 0): { x: number; y: number } {
    const p = this.ctx.player;
    const vy = p.onGround ? 0 : p.vy * 0.5;
    let t = Math.min(0.9, Math.hypot(p.x - fromX, p.y - fromY) / speed) + extraT;
    for (let i = 0; i < 2; i++) {
      const x = p.x + p.vx * t * k;
      const y = p.y + vy * t * k;
      t = Math.min(0.9, Math.hypot(x - fromX, y - fromY) / speed) + extraT;
    }
    return { x: p.x + Phaser.Math.Clamp(p.vx * t * k, -340, 340), y: p.y + Phaser.Math.Clamp(vy * t * k, -200, 200) };
  }

  /** A player shot is on course to hit this body within `range` px. */
  private incomingShot(range: number): boolean {
    let threat = false;
    const reach = this.body.height * 0.5 + 12;
    this.ctx.projectiles.forEach('player', (pr) => {
      if (threat || !pr.alive) return;
      const sp = Math.hypot(pr.vx, pr.vy);
      if (sp < 200) return;
      const rx = this.x - pr.x;
      const ry = this.y - pr.y;
      const along = (rx * pr.vx + ry * pr.vy) / sp;
      if (along < 0 || along > range) return;
      if (Math.abs(rx * pr.vy - ry * pr.vx) / sp < reach) threat = true;
    });
    return threat;
  }

  /**
   * Ground units: follow the player between floors. Jumps onto the platform the player stands on
   * (headroom permitting) and drops through one-way floors when the player is below.
   */
  private climb(dt: number, toward: number): void {
    const p = this.ctx.player;
    const b = this.body;
    if (!b.blocked.down || this.jumpCd > 0 || this.knockT > 0) return;
    const rise = this.feetY - p.feetY;
    const w = this.ctx.world;
    if (rise > 80 && p.onGround && Math.abs(p.x - this.x) < 460) {
      const h = Math.min(TROOPER_AI.maxJump, rise + 50);
      if (w.isSolidAt(this.x, b.top - 24) || w.isSolidAt(this.x + toward * 30, b.top - h * 0.6)) return;
      this.jumpCd = TROOPER_AI.jumpCooldown;
      b.setVelocityY(-Math.sqrt(2 * this.def.gravity * h));
      b.velocity.x = toward * this.speed() * 1.25;
      this.ctx.fx.smoke(this.x, this.feetY - 4, { count: 3 });
    } else if (rise < -80 && w.isOneWayAt(this.x, this.feetY + 6)) {
      this.jumpCd = 0.6;
      this.dropT = 0.3;
    }
  }

  private muzzle(): { x: number; y: number } {
    const rig = RIGS[this.defId];
    if (!this.gun || !rig.gun) return { x: this.x + this.facing * 20, y: this.y - 8 };
    const r = this.gun.rotation;
    const sx = this.gun.scaleX;
    const mx = rig.gun.muzzleX * sx * ENEMY_ART_SCALE;
    const my = rig.gun.muzzleY * this.gun.scaleY * ENEMY_ART_SCALE;
    return { x: this.gun.x + Math.cos(r) * mx - Math.sin(r) * my, y: this.gun.y + Math.sin(r) * mx + Math.cos(r) * my };
  }

  update(dt: number): void {
    if (!this.alive || this.spawning) return;
    this.t += dt;
    const p = this.ctx.player;
    const stunned = !!this.status('stun');
    if (this.knockT > 0) this.knockT -= dt;
    if (this.dropT > 0) this.dropT -= dt;
    if (this.dodgeCd > 0) this.dodgeCd -= dt;
    if (this.jumpCd > 0) this.jumpCd -= dt;

    if (stunned) {
      this.tele?.cancel();
      this.tele = null;
      if (!this.flying) this.body.velocity.x *= 0.85;
      else this.body.velocity.scale(0.9);
      this.play('stagger');
    } else if (p && p.alive) {
      switch (this.defId) {
        case 'trooper':
          this.aiShooter(dt, TROOPER_AI.preferredRange + this.rangeBias, TROOPER_AI.rangeSlack, TROOPER_AI.fireRange);
          break;
        case 'lancer':
          this.aiShooter(dt, LANCER_AI.idealRange + this.rangeBias, 200, LANCER_AI.fireRange);
          break;
        case 'hornet':
          this.aiHornet(dt);
          break;
        case 'bulwark':
          this.aiBulwark(dt);
          break;
      }
    } else if (!this.flying) {
      this.body.velocity.x *= 0.8;
      this.play('idle');
    }
    this.syncVisuals(dt);
  }

  private face(x: number): void {
    if (Math.abs(x - this.x) > 6) this.facing = x < this.x ? -1 : 1;
  }

  /** Walk toward `dir` (-1/0/1) without walking off ledges; hop small steps. */
  private walk(dir: number, dt: number): void {
    if (this.knockT > 0) return;
    const b = this.body;
    let target = dir * this.speed();
    if (dir !== 0) {
      const aheadX = this.x + dir * (b.width / 2 + 10);
      const w = this.ctx.world;
      const ground = w.isSolidAt(aheadX, this.feetY + 12) || w.isOneWayAt(aheadX, this.feetY + 12);
      // Chasing a player who is below: accept longer drops.
      const maxDrop = this.ctx.player.feetY > this.feetY + 80 ? TROOPER_AI.maxDrop * 1.6 : TROOPER_AI.maxDrop;
      const below = ground ? this.feetY : w.groundBelow(aheadX, this.feetY + 12, maxDrop);
      if (below === null && b.blocked.down) target = 0; // ledge too high to drop
      const wall = w.isSolidAt(aheadX, this.feetY - 24);
      if (wall && b.blocked.down) {
        // Small steps are hopped; anything up to jump height is vaulted (cover, pillars, crates).
        const h = this.obstacleHeight(aheadX);
        if (h <= TROOPER_AI.maxStep) b.setVelocityY(-TROOPER_AI.hopVelocity * 0.85);
        else if (h <= TROOPER_AI.maxJump && this.jumpCd <= 0 && !w.isSolidAt(this.x, b.top - 24)) {
          this.jumpCd = TROOPER_AI.jumpCooldown;
          b.setVelocityY(-Math.sqrt(2 * this.def.gravity * (h + 40)));
          this.ctx.fx.smoke(this.x, this.feetY - 4, { count: 3 });
        } else target = 0;
      }
    }
    const k = 1 - Math.exp(-dt * (b.blocked.down ? 10 : 4));
    b.velocity.x += (target - b.velocity.x) * k;
    this.play(Math.abs(b.velocity.x) > 20 ? 'move' : 'idle');
  }

  /** Height (px above the feet) of the solid obstacle at column x; Infinity when taller than a jump. */
  private obstacleHeight(x: number): number {
    const w = this.ctx.world;
    for (let h = 24; h <= TROOPER_AI.maxJump + 16; h += 16) if (!w.isSolidAt(x, this.feetY - h)) return h;
    return Infinity;
  }

  /**
   * Trooper and lancer: hold a (per-unit) range with line of sight, strafe so they are not static
   * targets, hop over incoming shots, hunt the player across floors when sight is lost, then
   * telegraph and fire at where the player is going to be. Lancers leap to high perches instead.
   */
  private aiShooter(dt: number, range: number, slack: number, fireRange: number): void {
    const p = this.ctx.player;
    const dx = p.x - this.x;
    const dist = Math.hypot(dx, p.y - this.y);
    const atk = this.def.attack;
    const isLancer = this.defId === 'lancer';
    const turnRate = isLancer ? LANCER_AI.aimTurnRate : TROOPER_AI.aimTurnRate;
    const sees = this.sight(dt);
    const lead = (k: number) => {
      const m = this.muzzle();
      const t = this.predict(m.x, m.y, atk.projectileSpeed, k);
      return Math.atan2(t.y - m.y, t.x - m.x);
    };

    if (this.phase === 'leap') {
      this.play('leap');
      this.aimAngle = rotateToward(this.aimAngle, lead(this.leadK), turnRate * dt);
      this.cooldown -= dt * this.stats.attackRate;
      this.phaseT -= dt;
      if ((this.phaseT < 0.9 && this.body.blocked.down) || this.phaseT <= 0) {
        this.ctx.fx.smoke(this.x, this.feetY - 4, { count: 4 });
        this.enter('move');
      }
      return;
    }

    if (this.phase === 'move' || this.phase === 'recover') {
      this.face(p.x);
      const hunting = this.noSightT > TROOPER_AI.huntAfter;
      // Lancer: relocate to a better perch when crowded, blind, or on its timer.
      if (isLancer && this.phase === 'move' && this.body.blocked.down) {
        this.perchT -= dt;
        const crowded = dist < LANCER_AI.retreatRange && this.ctx.rng.chance(LANCER_AI.escapeChance * dt * 4);
        if (crowded || this.noSightT > LANCER_AI.repositionAfter || this.perchT <= 0) {
          this.perchT = this.ctx.rng.float(LANCER_AI.perchInterval[0], LANCER_AI.perchInterval[1]);
          if (this.tryLeap()) return;
        }
      }
      let dir = 0;
      if (isLancer && dist < LANCER_AI.retreatRange) dir = -Math.sign(dx);
      else if (hunting) dir = Math.abs(dx) > 30 ? Math.sign(dx) : 0;
      else if (dist > range + slack) dir = Math.sign(dx);
      else if (dist < range - slack) dir = -Math.sign(dx);
      else if (!isLancer) {
        // Inside its band: keep moving so it is never a static target.
        this.strafeT -= dt;
        if (this.strafeT <= 0) {
          this.strafeT = this.ctx.rng.float(TROOPER_AI.strafeInterval[0], TROOPER_AI.strafeInterval[1]);
          this.strafeDir = this.ctx.rng.pick([-1, 0, 1, 1, -1]);
        }
        dir = this.strafeDir;
      }
      if (dir === 0) dir = this.mgr.crowding(this);
      this.walk(dir, dt);
      if (hunting || !isLancer) this.climb(dt, Math.sign(dx) || this.facing);
      // Hop over a shot that is about to hit.
      if (this.dodgeCd <= 0 && this.body.blocked.down && this.knockT <= 0) {
        if (this.incomingShot(TROOPER_AI.dodgeSense)) {
          const chance = TROOPER_AI.dodgeChance * (isLancer ? 0.6 : 1);
          this.dodgeCd = this.ctx.rng.chance(chance) ? TROOPER_AI.dodgeCooldown : 0.35;
          if (this.dodgeCd === TROOPER_AI.dodgeCooldown) {
            this.body.setVelocityY(-760);
            this.body.velocity.x = this.ctx.rng.pick([-1, 1]) * this.speed() * 1.4;
          }
        } else this.dodgeCd = 0.08;
      }
      this.aimAngle = rotateToward(this.aimAngle, Math.atan2(p.y - this.y, dx), turnRate * dt);
      this.cooldown -= dt * this.stats.attackRate;
      if (this.phase === 'recover') {
        this.phaseT -= dt;
        if (this.phaseT <= 0) this.enter('move');
      } else if (this.cooldown <= 0 && dist < fireRange && sees && this.body.blocked.down) {
        const r = isLancer ? LANCER_AI.lead : TROOPER_AI.lead;
        this.leadK = this.ctx.rng.float(r[0], r[1]);
        this.enter('windup', atk.windup);
        const m = this.muzzle();
        this.tele = this.ctx.fx.warnLine(m.x, m.y, p.x, p.y, atk.windup * 1000, { width: isLancer ? 5 : 4, color: isLancer ? COLORS.threat : COLORS.telegraph });
        this.ctx.fx.warnMarker(this.x, this.body.top - 18, atk.windup * 1000, { follow: this.obj });
        this.ctx.audio.play(isLancer ? 'enemy_laser_charge' : 'enemy_telegraph', { volume: 0.7 });
      }
      return;
    }

    if (this.phase === 'windup') {
      this.walk(0, dt);
      this.play('windup');
      this.phaseT -= dt;
      const locked = this.phaseT <= atk.lock;
      if (!locked) {
        this.face(p.x);
        this.aimAngle = rotateToward(this.aimAngle, lead(this.leadK), turnRate * 1.6 * dt);
        this.lockX = p.x;
        this.lockY = p.y;
      }
      const m = this.muzzle();
      const len = isLancer ? 1600 : Math.max(200, Math.hypot(this.lockX - m.x, this.lockY - m.y));
      this.tele?.update({ x: m.x, y: m.y, x2: m.x + Math.cos(this.aimAngle) * len, y2: m.y + Math.sin(this.aimAngle) * len });
      if (this.phaseT <= 0) {
        this.lockAngle = this.aimAngle;
        this.rawAngle = Math.atan2(this.lockY - m.y, this.lockX - m.x);
        this.shots = atk.count + this.stats.extraShots;
        this.shotT = 0;
        this.enter('attack');
      }
      return;
    }

    if (this.phase === 'attack') {
      this.walk(0, dt);
      this.play('attack');
      this.shotT -= dt;
      if (this.shotT <= 0 && this.shots > 0) {
        const n = atk.count + this.stats.extraShots - this.shots;
        this.shots--;
        this.shotT = atk.interval;
        const m = this.muzzle();
        // Trooper bursts bracket the target: led shot, un-led shot, then an over-led one, so
        // neither holding a line nor stopping dead is a safe answer.
        const off = Phaser.Math.Clamp(Phaser.Math.Angle.Wrap(this.lockAngle - this.rawAngle), -0.22, 0.22);
        const angle = isLancer ? this.lockAngle : this.lockAngle + [0, -off, off * 0.6][n % 3] + this.ctx.rng.float(-0.02, 0.02);
        this.ctx.projectiles.spawn({
          owner: 'enemy',
          x: m.x,
          y: m.y,
          angle,
          speed: atk.projectileSpeed,
          damage: this.stats.attackDamage,
          tags: [],
          texture: isLancer ? TEX.projEnemyNeedle : TEX.projEnemyBolt,
          radius: isLancer ? LANCER_AI.needleRadius : TROOPER_AI.boltRadius,
          lifetime: isLancer ? 1.4 : 2.2,
          glow: true,
          trail: 'enemy',
          knockback: isLancer ? 420 : 220,
        });
        this.ctx.fx.muzzleFlash(m.x, m.y, angle, COLORS.threat, isLancer ? 1.2 : 0.8);
        this.ctx.audio.play(isLancer ? 'enemy_laser_fire' : 'enemy_shoot', { volume: 0.6 });
      }
      if (this.shots <= 0 && this.shotT <= 0) {
        this.cooldown = this.ctx.rng.float(atk.cooldown[0], atk.cooldown[1]);
        this.enter('recover', atk.recover);
      }
      return;
    }

    if (this.phase === 'stagger') {
      this.walk(0, dt);
      this.play('stagger');
      this.phaseT -= dt;
      if (this.phaseT <= 0) this.enter('move');
    }
  }

  /** Perch value of standing at (x, feetY): sniping distance, height over the player, clear sight. */
  private perchScore(x: number, feetY: number): number {
    const p = this.ctx.player;
    const eyeY = feetY - 50;
    const d = Math.hypot(p.x - x, p.y - eyeY);
    let s = -Math.abs(d - LANCER_AI.idealRange) * 0.25 + Phaser.Math.Clamp(p.feetY - feetY, -200, 320) * 0.4;
    if (d < LANCER_AI.retreatRange + 60) s -= 220;
    if (this.ctx.world.raycast(x, eyeY, p.x, p.y).hit) s -= 180;
    return s;
  }

  /** Lancer: jet-leap to the best reachable perch (ballistic arc), if one beats where it stands. */
  private tryLeap(): boolean {
    const w = this.ctx.world;
    const g = this.def.gravity;
    let best: { x: number; y: number } | null = null;
    let bestScore = this.perchScore(this.x, this.feetY) + 45;
    for (let i = 0; i < 16; i++) {
      const off = (i % 2 ? -1 : 1) * (140 + Math.floor(i / 2) * 60);
      if (Math.abs(off) > LANCER_AI.leapReachX) continue;
      const x = this.x + off;
      const gy = w.groundBelow(x, this.feetY - LANCER_AI.leapReachY, LANCER_AI.leapReachY + 260);
      if (gy === null) continue;
      // Headroom at the landing spot, and nothing solid along the straight line up to it.
      if (w.isSolidAt(x, gy - 30) || w.isSolidAt(x, gy - 80)) continue;
      if (w.raycast(this.x, this.body.top - 30, x, gy - this.body.height - 30).hit) continue;
      const s = this.perchScore(x, gy);
      if (s > bestScore) {
        bestScore = s;
        best = { x, y: gy };
      }
    }
    if (!best) return false;
    const dx = best.x - this.x;
    const T = 0.55 + Math.abs(dx) / 1800;
    const dy = best.y - this.feetY;
    this.body.setVelocity(dx / T, (dy - 0.5 * g * T * T) / T);
    this.facing = dx < 0 ? -1 : 1;
    this.tele?.cancel();
    this.tele = null;
    this.enter('leap', T + 1);
    this.ctx.fx.smoke(this.x, this.feetY - 4, { count: 6 });
    this.ctx.fx.vent(this.x, this.feetY - 10, Math.PI / 2);
    this.ctx.audio.play('jump', { volume: 0.5, pitch: 0.8 });
    return true;
  }

  /**
   * Hornet dive length: through the locked point plus the overshoot, but pulling out just short of
   * the floor or a wall (a dive straight into the ground only stunned itself).
   */
  private diveLength(ang: number): number {
    const want = Math.hypot(this.lockX - this.x, this.lockY - this.y) + HORNET_AI.overshoot;
    const ray = this.ctx.world.raycast(this.x, this.y, this.x + Math.cos(ang) * want, this.y + Math.sin(ang) * want);
    return ray.hit ? Math.max(80, ray.dist - this.body.height) : want;
  }

  /** Hornet: flank-orbit, pull back, dive through where the player will be, chain a second dive after a miss. */
  private aiHornet(dt: number): void {
    const p = this.ctx.player;
    const b = this.body;
    const atk = this.def.attack;
    const sees = this.sight(dt);
    this.buzzT -= dt;
    if (this.buzzT <= 0) {
      this.buzzT = this.ctx.rng.float(HORNET_AI.buzzInterval[0], HORNET_AI.buzzInterval[1]);
      this.ctx.audio.play('drone_buzz', { volume: 0.35 });
    }
    const steer = (tx: number, ty: number, maxSp: number) => {
      const dx = tx - this.x;
      const dy = ty - this.y;
      const d = Math.hypot(dx, dy) || 1;
      const want = Math.min(maxSp, d * 4);
      const vx = (dx / d) * want;
      const vy = (dy / d) * want;
      const k = Math.min(1, (HORNET_AI.accel * dt) / Math.max(1, Math.hypot(vx - b.velocity.x, vy - b.velocity.y)));
      b.velocity.x += (vx - b.velocity.x) * k;
      b.velocity.y += (vy - b.velocity.y) * k;
    };
    const startDive = (windup: number): void => {
      this.leadK = this.ctx.rng.float(HORNET_AI.lead[0], HORNET_AI.lead[1]);
      const t = this.predict(this.x, this.y, atk.projectileSpeed, this.leadK, windup);
      this.lockX = t.x;
      this.lockY = t.y;
      this.enter('windup', windup);
      const ang = Math.atan2(this.lockY - this.y, this.lockX - this.x);
      const len = this.diveLength(ang);
      this.tele = this.ctx.fx.warnLine(this.x, this.y, this.x + Math.cos(ang) * len, this.y + Math.sin(ang) * len, windup * 1000, { width: 8 });
      this.ctx.audio.play('enemy_telegraph', { volume: 0.6, pitch: 1.3 });
    };

    if (this.phase === 'move' || this.phase === 'recover') {
      // Circle round to the side the player is not facing (flanking), slower once there.
      const inFront = Math.sign(this.x - p.x) === p.facing;
      this.orbit += HORNET_AI.orbitDrift * (inFront ? HORNET_AI.flankDrift : 1) * dt;
      const r = (HORNET_AI.orbitMin + HORNET_AI.orbitMax) / 2 + Math.sin(this.t * 0.7) * 30;
      const tx = p.x + Math.cos(this.orbit) * r;
      const ty = p.y - 160 + Math.sin(this.orbit * 1.3) * 60 + Math.sin(this.t * HORNET_AI.bobFreq) * HORNET_AI.bobAmp;
      if (this.knockT <= 0) steer(tx, ty, this.speed());
      this.face(p.x);
      this.play('move');
      if (this.phase === 'recover') {
        this.phaseT -= dt;
        if (this.phaseT <= 0) this.enter('move');
      }
      this.cooldown -= dt * this.stats.attackRate;
      if (this.phase === 'move' && this.cooldown <= 0 && Math.hypot(p.x - this.x, p.y - this.y) < 660 && sees) {
        this.chain = 0;
        startDive(atk.windup);
      }
      return;
    }

    if (this.phase === 'windup') {
      // Track (with lead) until the lock, then commit.
      if (this.phaseT > atk.lock) {
        const t = this.predict(this.x, this.y, atk.projectileSpeed, this.leadK, this.phaseT);
        this.lockX = t.x;
        this.lockY = t.y;
      }
      const ang = Math.atan2(this.lockY - this.y, this.lockX - this.x);
      b.setVelocity(-Math.cos(ang) * HORNET_AI.pullBackSpeed, -Math.sin(ang) * HORNET_AI.pullBackSpeed);
      this.face(this.lockX);
      this.play('windup');
      const len = this.diveLength(ang);
      this.tele?.update({ x: this.x, y: this.y, x2: this.x + Math.cos(ang) * len, y2: this.y + Math.sin(ang) * len });
      this.phaseT -= dt;
      if (this.phaseT <= 0) {
        this.lockAngle = ang;
        this.dashLeft = len;
        this.enter('attack');
        b.setVelocity(Math.cos(ang) * atk.projectileSpeed, Math.sin(ang) * atk.projectileSpeed);
      }
      return;
    }

    if (this.phase === 'attack') {
      this.play('attack');
      this.dashLeft -= atk.projectileSpeed * dt;
      b.setVelocity(Math.cos(this.lockAngle) * atk.projectileSpeed, Math.sin(this.lockAngle) * atk.projectileSpeed);
      const bonk = b.blocked.left || b.blocked.right || b.blocked.down || b.blocked.up;
      if (bonk) {
        this.ctx.fx.sparks(this.x, this.y, this.lockAngle + Math.PI, { count: 8 });
        this.enter('stagger');
        this.phaseT = HORNET_AI.bonkStun;
      } else if (this.dashLeft <= 0) {
        // Missed: sometimes wheel round for an immediate second pass.
        const chains = this.elite.includes('overcharged') || this.ctx.rng.chance(HORNET_AI.chainChance);
        if (this.chain < HORNET_AI.maxChain && chains && this.canSee()) {
          this.chain++;
          b.velocity.scale(0.25);
          startDive(HORNET_AI.chainWindup);
        } else {
          this.cooldown = this.ctx.rng.float(atk.cooldown[0], atk.cooldown[1]);
          this.enter('recover', atk.recover);
          b.setVelocity(b.velocity.x * 0.3, -HORNET_AI.recoverRise);
        }
      }
      return;
    }

    if (this.phase === 'stagger') {
      b.velocity.scale(0.9);
      b.velocity.y += 300 * dt;
      this.play('stagger');
      this.phaseT -= dt;
      if (this.phaseT <= 0) {
        this.cooldown = this.ctx.rng.float(atk.cooldown[0], atk.cooldown[1]);
        this.enter('recover', atk.recover);
      }
    }
  }

  /** Bulwark: advance behind the shield, telegraphed bash, vent with the shield down. */
  private aiBulwark(dt: number): void {
    const p = this.ctx.player;
    const atk = this.def.attack;
    const dx = p.x - this.x;
    const dy = p.y - this.y;

    if (this.phase === 'move') {
      // Turn toward the player once they stay behind; almost at once when they vault over it.
      if (Math.sign(dx) !== this.facing && Math.abs(dx) > 30) {
        this.turnT += dt;
        const vaulted = dy < -60 || Math.abs(dx) < 140;
        if (this.turnT > (vaulted ? BULWARK_AI.vaultTurnDelay : BULWARK_AI.turnDelay)) {
          this.facing = this.facing === 1 ? -1 : 1;
          this.turnT = 0;
        }
      } else this.turnT = 0;
      this.walk(Math.abs(dx) > 90 ? this.facing : 0, dt);
      this.climb(dt, this.facing);
      this.cooldown -= dt * this.stats.attackRate;
      const inFront = dx * this.facing > BULWARK_AI.bashMinRange && dx * this.facing < BULWARK_AI.bashMaxRange;
      if (this.cooldown <= 0 && inFront && Math.abs(dy) < BULWARK_AI.bashMaxDy && this.body.blocked.down) {
        this.enter('windup', atk.windup);
        const x0 = this.facing > 0 ? this.x : this.x - BULWARK_AI.bashDistance;
        this.tele = this.ctx.fx.warnRect(x0, this.feetY - this.body.height, BULWARK_AI.bashDistance, this.body.height, atk.windup * 1000);
        this.ctx.audio.play('enemy_telegraph', { volume: 0.8, pitch: 0.7 });
      }
      return;
    }
    if (this.phase === 'windup') {
      this.walk(0, dt);
      this.play('windup');
      // Keep facing the player through the wind-up (the bash goes where they are).
      if (Math.sign(dx) !== this.facing && Math.abs(dx) > 40) {
        this.facing = this.facing === 1 ? -1 : 1;
        const x0 = this.facing > 0 ? this.x : this.x - BULWARK_AI.bashDistance;
        this.tele?.update({ x: x0, y: this.feetY - this.body.height });
      }
      this.phaseT -= dt;
      if (this.phaseT <= 0) {
        this.dashDir = this.facing;
        this.dashLeft = BULWARK_AI.bashDistance;
        this.enter('attack');
      }
      return;
    }
    if (this.phase === 'attack') {
      this.play('attack');
      const step = BULWARK_AI.bashSpeed * dt;
      this.dashLeft -= step;
      this.body.setVelocityX(this.dashDir * BULWARK_AI.bashSpeed);
      if (this.dashLeft <= 0 || this.body.blocked.left || this.body.blocked.right) {
        this.ctx.fx.shake(0.004, 120);
        this.ctx.fx.smoke(this.x + this.dashDir * 30, this.feetY - 10, { count: 4 });
        this.body.setVelocityX(0);
        this.cooldown = this.ctx.rng.float(atk.cooldown[0], atk.cooldown[1]);
        this.enter('recover', atk.recover);
      }
      return;
    }
    // recover / stagger: shield down, vulnerable.
    this.walk(0, dt);
    this.play('stagger');
    this.phaseT -= dt;
    if (this.phaseT <= 0) {
      // Recovered facing away from the player: turn at once.
      if (Math.sign(dx) !== this.facing && Math.abs(dx) > 30) this.facing = this.facing === 1 ? -1 : 1;
      this.enter('move');
    }
  }

  /** Damage dealt by touching this enemy right now (bash hits harder). */
  contactDamage(): number {
    if (this.defId === 'bulwark' && this.phase === 'attack') return this.stats.attackDamage;
    if (this.defId === 'hornet' && this.phase === 'attack') return this.stats.attackDamage;
    return this.stats.contactDamage;
  }

  contactKnockback(): number {
    return this.defId === 'bulwark' && this.phase === 'attack' ? BULWARK_AI.bashKnockback : 520;
  }

  private syncVisuals(dt: number): void {
    const rig = RIGS[this.defId];
    const s = this.stats.scale * ENEMY_SIZE[this.defId].view;
    /** Sprite scale (sheets are baked at ENEMY_ART_SCALE). */
    const vs = s / ENEMY_ART_SCALE;
    const fx = this.flying ? this.x : this.x;
    const fy = this.flying ? this.y : this.feetY;
    this.sprite.setPosition(fx, fy).setScale(this.facing * vs, vs);
    const over = this.status('overheated');
    if (over) this.sprite.setTint(0xffb080);
    else if (this.sprite.isTinted && !this.sprite.tintFill) this.sprite.clearTint();
    if (this.outline) {
      const name = outlineFrame(String(this.sprite.frame.name));
      if (this.outline.texture.has(name)) this.outline.setFrame(name);
      this.outline.setPosition(fx, fy).setScale(this.facing * vs, vs).setAlpha(0.55 + 0.35 * Math.sin(this.t * 6));
    }
    const idx = Math.max(0, (this.sprite.anims.currentFrame?.index ?? 1) - 1);
    const meta = this.ctx.scene.textures.exists(rig.key) ? frameMeta(this.defId, this.anim, idx) : null;
    if (this.gun && meta && rig.gun) {
      this.gun.setPosition(fx + meta.pivot.x * s * this.facing, fy + meta.pivot.y * s);
      // Aim in local (right-facing) space, clamped to the rig limits.
      const local = this.facing > 0 ? this.aimAngle : Math.PI - this.aimAngle;
      const clamped = Phaser.Math.Clamp(Phaser.Math.Angle.Wrap(local), rig.gun.minAngle, rig.gun.maxAngle);
      const aiming = this.phase !== 'move' || this.defId === 'lancer' || this.defId === 'trooper';
      const a = aiming ? clamped : rig.gun.restAngle;
      this.gun.setScale(this.facing * vs, vs).setRotation(this.facing > 0 ? a : -a);
    }
    if (this.shield && meta) {
      const down = this.phase === 'recover' || this.phase === 'stagger';
      this.shield.setPosition(fx + meta.pivot.x * s * this.facing, fy + meta.pivot.y * s + (down ? 26 : 0));
      this.shield.setScale(this.facing * vs, vs).setRotation(down ? this.facing * 0.5 : 0).setAlpha(down ? 0.45 : 1);
    }
    if (this.hex) this.hex.setPosition(this.x, this.y).setAlpha(0.25 + 0.5 * (this.armorHp / Math.max(1, this.stats.maxArmor)));
    const top = this.body.top - 10;
    this.label?.setPosition(this.x, top - 10);
    if (this.elite.includes('swift')) {
      this.swiftT -= dt;
      if (this.swiftT <= 0 && Math.abs(this.body.velocity.x) > 80) {
        this.swiftT = 0.08;
        this.ctx.fx.afterimage(this.sprite, { color: ELITE_AFFIXES_DEF.swift.color, alpha: 0.35, duration: 200 });
      }
    }
    this.barTimer -= dt;
    const showBar = this.barTimer > 0 && this.hp < this.maxHp;
    this.barBg.setVisible(showBar).setPosition(this.x, top);
    this.bar.setVisible(showBar).setPosition(this.x - 22, top).setSize(44 * Math.max(0, this.hp / this.maxHp), 3);
  }
}

function rotateToward(cur: number, target: number, maxStep: number): number {
  const d = Phaser.Math.Angle.Wrap(target - cur);
  return cur + Phaser.Math.Clamp(d, -maxStep, maxStep);
}

class EnemyManager implements EnemyManagerApi {
  readonly group: Phaser.Physics.Arcade.Group;
  private items: EnemyApi[] = [];
  private own = new Set<Enemy>();
  private pending: { e: Enemy; t: number }[] = [];
  private contactCd = new Map<number, number>();

  constructor(private readonly ctx: GameContext) {
    ensureEnemyArt(ctx.scene);
    this.group = ctx.scene.physics.add.group({ allowGravity: false });
  }

  get list(): readonly EnemyApi[] {
    return this.items;
  }

  spawn(id: EnemyId, x: number, y: number, opts: { elite?: EliteAffix[]; portalMs?: number; level?: number } = {}): EnemyApi {
    const ctx = this.ctx;
    const def = ENEMIES[id] ?? ENEMIES.trooper;
    const stats = computeEnemyStats(def, { difficulty: ctx.world?.sector?.difficulty ?? 1, level: opts.level ?? 0, elite: opts.elite ?? [] });
    const e = new Enemy(ctx, this, def.id, x, y, opts.elite ?? [], stats);
    this.own.add(e);
    this.items.push(e);
    this.group.add(e.obj);
    // Group membership resets body settings: reapply gravity.
    e.body.setAllowGravity(!def.flying);
    e.body.setGravityY(def.flying ? 0 : def.gravity);
    try {
      // Flyers pass through one-way platforms; ground units drop through them on purpose.
      ctx.world.addCollider(e.obj, { canDrop: () => e.flying || e.dropping });
    } catch {
      /* world not ready (sandbox) */
    }
    if (opts.portalMs && opts.portalMs > 0) {
      e.setSpawning(true);
      {
        const k = ENEMY_SIZE[id].body;
        ctx.fx.spawnPortal(x, def.flying ? y : y - (def.body.h * k) / 2, opts.portalMs, { radius: Math.max(def.body.w, def.body.h) * k * 0.8 });
      }
      ctx.audio.play('enemy_spawn', { volume: 0.5 });
      this.pending.push({ e, t: opts.portalMs / 1000 });
    } else {
      ctx.bus.emit('enemy:spawned', { enemy: e });
    }
    return e;
  }

  register(e: EnemyApi): void {
    if (!this.items.includes(e)) this.items.push(e);
    const body = e.obj as Phaser.GameObjects.GameObject & { body?: unknown };
    if (body.body && !this.group.contains(e.obj)) {
      this.group.add(e.obj);
    }
  }

  unregister(e: EnemyApi): void {
    const i = this.items.indexOf(e);
    if (i >= 0) this.items.splice(i, 1);
    if (this.group.contains(e.obj)) this.group.remove(e.obj, false, false);
  }

  /** Direction (-1/0/1) a ground unit should step to stop standing inside another one. */
  crowding(self: Enemy): number {
    let push = 0;
    for (const o of this.own) {
      if (o === self || !o.alive || o.spawning || o.flying) continue;
      const dx = self.x - o.x;
      if (Math.abs(dx) < 48 && Math.abs(self.y - o.y) < 70) push += dx === 0 ? (self.uid > o.uid ? 1 : -1) : Math.sign(dx);
    }
    return Math.sign(push);
  }

  nearest(x: number, y: number, maxDist = Infinity, exclude?: Set<EnemyApi>): EnemyApi | null {
    let best: EnemyApi | null = null;
    let bd = maxDist;
    for (const e of this.items) {
      if (!e.alive || e.spawning || e.kind === 'prop' || exclude?.has(e)) continue;
      const d = Math.hypot(e.x - x, e.y - y);
      if (d < bd) {
        bd = d;
        best = e;
      }
    }
    return best;
  }

  inRadius(x: number, y: number, r: number, includeProps = false): EnemyApi[] {
    return this.items.filter((e) => e.alive && !e.spawning && (includeProps || e.kind !== 'prop') && Math.hypot(e.x - x, e.y - y) <= r + e.radius);
  }

  aliveCount(): number {
    let n = 0;
    for (const e of this.items) if (e.alive && e.kind !== 'prop') n++;
    return n;
  }

  killAll(): void {
    for (const e of [...this.items]) {
      if (e.kind === 'prop' || !e.alive) continue;
      if (e instanceof Enemy && e.spawning) e.setSpawning(false);
      this.ctx.damage.hitEnemy(e, { amount: e.hp + e.armorHp + 1, tags: [], source: 'player', label: 'debug' });
    }
  }

  update(dt: number): void {
    for (let i = this.pending.length - 1; i >= 0; i--) {
      const p = this.pending[i];
      p.t -= dt;
      if (p.t <= 0) {
        this.pending.splice(i, 1);
        if (!p.e.alive) continue;
        p.e.setSpawning(false);
        this.ctx.bus.emit('enemy:spawned', { enemy: p.e });
      }
    }
    for (const e of this.own) {
      if (e.dying && !e.obj.active) this.own.delete(e);
      else e.update(dt);
    }
    this.separate();
    // Drop entries that died outside the damage pipeline (props, bosses).
    for (let i = this.items.length - 1; i >= 0; i--) if (!this.items[i].alive) this.items.splice(i, 1);
    this.contactDamage(dt);
  }

  /**
   * Ground enemies have no body-to-body collision, so units heading for the same player would merge
   * into one stack. Overlapping pairs on the same floor are pushed apart sideways (velocity, so walls
   * still stop them), harder the deeper they overlap.
   */
  private separate(): void {
    const list = [...this.own].filter((e) => e.alive && !e.spawning && !e.flying);
    for (let i = 0; i < list.length; i++) {
      for (let j = i + 1; j < list.length; j++) {
        const a = list[i];
        const b = list[j];
        if (Math.abs(a.feetY - b.feetY) > 40) continue;
        const want = (a.body.width + b.body.width) * 0.5 + 10;
        const dx = b.x - a.x;
        const overlap = want - Math.abs(dx);
        if (overlap <= 0) continue;
        const dir = dx !== 0 ? Math.sign(dx) : a.uid < b.uid ? 1 : -1;
        const push = Math.min(260, overlap * 9);
        a.body.velocity.x -= dir * push * (1 - a.knockbackResist * 0.5);
        b.body.velocity.x += dir * push * (1 - b.knockbackResist * 0.5);
      }
    }
  }

  private contactDamage(dt: number): void {
    const p = this.ctx.player;
    if (!p || !p.alive) return;
    for (const [k, v] of this.contactCd) this.contactCd.set(k, v - dt);
    const pb = p.body;
    for (const e of this.own) {
      if (!e.alive || e.spawning) continue;
      if ((this.contactCd.get(e.uid) ?? 0) > 0) continue;
      if (e.statuses.get('stun')?.remaining) continue;
      const b = e.body;
      if (b.right < pb.left || b.left > pb.right || b.bottom < pb.top || b.top > pb.bottom) continue;
      const res = this.ctx.damage.hitPlayer({
        amount: e.contactDamage(),
        tags: [],
        source: 'enemy',
        sourceX: e.x,
        sourceY: e.y,
        knockback: e.contactKnockback(),
        label: 'contact',
      });
      if (!res.blocked) this.contactCd.set(e.uid, 0.5);
    }
  }

  destroy(): void {
    for (const e of this.own) e.destroy();
    this.own.clear();
    this.items = [];
    this.pending = [];
  }
}

export function createEnemies(ctx: GameContext): EnemyManagerApi {
  return new EnemyManager(ctx);
}
